import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingStorefrontIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';

// ── helpers ──────────────────────────────────────────────────────────────────

const BILLING_CYCLE_LABELS = { 1: 'Monthly', 2: 'Quarterly', 3: 'Annual', 4: 'One-off' };
const CONTRACT_STATUS_LABELS = { 1: 'Active', 2: 'Expired', 3: 'Cancelled', 4: 'Pending' };
const INVOICE_STATUS_LABELS = { 1: 'Pending', 2: 'Paid', 3: 'Overdue' };

const contractStatusBadge = (status) => {
  const colors = { 1: 'bg-green-100 text-green-800', 2: 'bg-gray-100 text-gray-600', 3: 'bg-red-100 text-red-800', 4: 'bg-yellow-100 text-yellow-800' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}>{CONTRACT_STATUS_LABELS[status] ?? status}</span>;
};

const invoiceStatusBadge = (status) => {
  const map = {
    1: { color: 'bg-yellow-100 text-yellow-800', Icon: ClockIcon },
    2: { color: 'bg-green-100 text-green-800', Icon: CheckCircleIcon },
    3: { color: 'bg-red-100 text-red-800', Icon: ExclamationCircleIcon },
  };
  const { color, Icon } = map[status] ?? { color: 'bg-gray-100 text-gray-600', Icon: ClockIcon };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {INVOICE_STATUS_LABELS[status] ?? status}
    </span>
  );
};

const formatCurrency = (amount, currency = 'ZAR') =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount ?? 0);

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ── empty form shapes ─────────────────────────────────────────────────────────

const emptySupplier = { name: '', contactName: '', contactEmail: '', contactPhone: '', website: '', notes: '' };
const emptyContract = { supplierId: 0, serviceName: '', description: '', costAmount: '', currency: 'ZAR', billingCycle: 1, contractStartDate: '', contractEndDate: '', autoRenew: false, status: 1, notes: '' };
const emptyInvoice = { supplierId: 0, supplierContractId: '', invoiceNumber: '', invoiceDate: '', dueDate: '', amount: '', currency: 'ZAR', status: 1, notes: '' };

// ── main component ────────────────────────────────────────────────────────────

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('contracts');
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // supplier form
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  const [savingSupplier, setSavingSupplier] = useState(false);

  // contract form
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm, setContractForm] = useState(emptyContract);
  const [savingContract, setSavingContract] = useState(false);

  // invoice form
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoice);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [savingInvoice, setSavingInvoice] = useState(false);

  // ── data loading ──────────────────────────────────────────────────────────

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getSuppliers();
      setSuppliers(data);
    } catch {
      setError('Failed to load suppliers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const loadSupplierDetail = useCallback(async (supplier) => {
    setSelectedSupplier(supplier);
    try {
      const [c, i] = await Promise.all([
        apiService.getSupplierContracts(supplier.id),
        apiService.getSupplierInvoices(supplier.id),
      ]);
      setContracts(c);
      setInvoices(i);
    } catch {
      setError('Failed to load supplier details.');
    }
  }, []);

  // ── supplier CRUD ─────────────────────────────────────────────────────────

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    setSavingSupplier(true);
    try {
      if (editingSupplier) {
        await apiService.updateSupplier(editingSupplier.id, supplierForm);
      } else {
        await apiService.createSupplier(supplierForm);
      }
      await loadSuppliers();
      setShowSupplierForm(false);
      setEditingSupplier(null);
      setSupplierForm(emptySupplier);
    } catch {
      alert('Failed to save supplier.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name,
      contactName: supplier.contactName ?? '',
      contactEmail: supplier.contactEmail ?? '',
      contactPhone: supplier.contactPhone ?? '',
      website: supplier.website ?? '',
      notes: supplier.notes ?? '',
    });
    setShowSupplierForm(true);
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Delete "${supplier.name}" and all its contracts and invoices?`)) return;
    try {
      await apiService.deleteSupplier(supplier.id);
      if (selectedSupplier?.id === supplier.id) setSelectedSupplier(null);
      await loadSuppliers();
    } catch {
      alert('Failed to delete supplier.');
    }
  };

  // ── contract CRUD ─────────────────────────────────────────────────────────

  const handleContractSubmit = async (e) => {
    e.preventDefault();
    setSavingContract(true);
    try {
      const payload = {
        ...contractForm,
        supplierId: selectedSupplier.id,
        costAmount: contractForm.costAmount === '' ? null : parseFloat(contractForm.costAmount),
        contractStartDate: contractForm.contractStartDate || null,
        contractEndDate: contractForm.contractEndDate || null,
      };
      if (editingContract) {
        await apiService.updateSupplierContract(editingContract.id, payload);
      } else {
        await apiService.createSupplierContract(payload);
      }
      const updated = await apiService.getSupplierContracts(selectedSupplier.id);
      setContracts(updated);
      setShowContractForm(false);
      setEditingContract(null);
      setContractForm(emptyContract);
    } catch {
      alert('Failed to save contract.');
    } finally {
      setSavingContract(false);
    }
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setContractForm({
      serviceName: contract.serviceName,
      description: contract.description ?? '',
      costAmount: contract.costAmount ?? '',
      currency: contract.currency ?? 'ZAR',
      billingCycle: contract.billingCycle,
      contractStartDate: contract.contractStartDate ? contract.contractStartDate.substring(0, 10) : '',
      contractEndDate: contract.contractEndDate ? contract.contractEndDate.substring(0, 10) : '',
      autoRenew: contract.autoRenew,
      status: contract.status,
      notes: contract.notes ?? '',
    });
    setShowContractForm(true);
  };

  const handleDeleteContract = async (contract) => {
    if (!window.confirm(`Delete contract "${contract.serviceName}"?`)) return;
    try {
      await apiService.deleteSupplierContract(contract.id);
      setContracts(contracts.filter((c) => c.id !== contract.id));
    } catch {
      alert('Failed to delete contract.');
    }
  };

  // ── invoice CRUD ──────────────────────────────────────────────────────────

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setSavingInvoice(true);
    try {
      if (editingInvoice) {
        const payload = {
          ...invoiceForm,
          supplierContractId: invoiceForm.supplierContractId || null,
          amount: parseFloat(invoiceForm.amount),
          invoiceDate: invoiceForm.invoiceDate || null,
          dueDate: invoiceForm.dueDate || null,
        };
        await apiService.updateSupplierInvoice(editingInvoice.id, payload);
      } else {
        const formData = new FormData();
        formData.append('supplierId', selectedSupplier.id);
        if (invoiceForm.supplierContractId) formData.append('supplierContractId', invoiceForm.supplierContractId);
        if (invoiceForm.invoiceNumber) formData.append('invoiceNumber', invoiceForm.invoiceNumber);
        if (invoiceForm.invoiceDate) formData.append('invoiceDate', invoiceForm.invoiceDate);
        if (invoiceForm.dueDate) formData.append('dueDate', invoiceForm.dueDate);
        formData.append('amount', parseFloat(invoiceForm.amount));
        formData.append('currency', invoiceForm.currency);
        formData.append('status', invoiceForm.status);
        if (invoiceForm.notes) formData.append('notes', invoiceForm.notes);
        if (invoiceFile) formData.append('file', invoiceFile);
        await apiService.createSupplierInvoice(formData);
      }
      const updated = await apiService.getSupplierInvoices(selectedSupplier.id);
      setInvoices(updated);
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      setInvoiceForm(emptyInvoice);
      setInvoiceFile(null);
    } catch {
      alert('Failed to save invoice.');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      supplierContractId: invoice.supplierContractId ?? '',
      invoiceNumber: invoice.invoiceNumber ?? '',
      invoiceDate: invoice.invoiceDate ? invoice.invoiceDate.substring(0, 10) : '',
      dueDate: invoice.dueDate ? invoice.dueDate.substring(0, 10) : '',
      amount: invoice.amount ?? '',
      currency: invoice.currency ?? 'ZAR',
      status: invoice.status,
      notes: invoice.notes ?? '',
    });
    setShowInvoiceForm(true);
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice "${invoice.invoiceNumber || `#${invoice.id}`}"?`)) return;
    try {
      await apiService.deleteSupplierInvoice(invoice.id);
      setInvoices(invoices.filter((i) => i.id !== invoice.id));
    } catch {
      alert('Failed to delete invoice.');
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const blob = await apiService.downloadSupplierInvoiceFile(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = invoice.fileName || 'invoice';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download invoice file.');
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BuildingStorefrontIcon className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
            <p className="text-sm text-gray-500">Manage supplier contracts and invoices</p>
          </div>
        </div>
        <button
          onClick={() => { setShowSupplierForm(true); setEditingSupplier(null); setSupplierForm(emptySupplier); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
        >
          <PlusIcon className="h-4 w-4" />
          Add Supplier
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {/* Two-column layout */}
      <div className="flex gap-6 h-full" style={{ minHeight: '600px' }}>
        {/* Left: supplier list */}
        <div className="w-72 flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto">
          {suppliers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <BuildingStorefrontIcon className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No suppliers yet</p>
              <p className="text-xs mt-1">Add your first supplier to get started</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {suppliers.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => loadSupplierDetail(s)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${selectedSupplier?.id === s.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${selectedSupplier?.id === s.id ? 'text-blue-700' : 'text-gray-900'}`}>{s.name}</p>
                      {s.contactName && <p className="text-xs text-gray-500 truncate">{s.contactName}</p>}
                      <div className="flex gap-2 mt-1">
                        {s.contractCount > 0 && <span className="text-xs text-gray-400">{s.contractCount} contract{s.contractCount !== 1 ? 's' : ''}</span>}
                        {s.invoiceCount > 0 && <span className="text-xs text-gray-400">{s.invoiceCount} invoice{s.invoiceCount !== 1 ? 's' : ''}</span>}
                      </div>
                    </div>
                    <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 ml-2 ${selectedSupplier?.id === s.id ? 'text-blue-600' : 'text-gray-300'}`} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: detail panel */}
        {selectedSupplier ? (
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            {/* Supplier header card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedSupplier.name}</h2>
                  <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-600">
                    {selectedSupplier.contactName && <span><span className="font-medium">Contact:</span> {selectedSupplier.contactName}</span>}
                    {selectedSupplier.contactEmail && <span><span className="font-medium">Email:</span> {selectedSupplier.contactEmail}</span>}
                    {selectedSupplier.contactPhone && <span><span className="font-medium">Phone:</span> {selectedSupplier.contactPhone}</span>}
                    {selectedSupplier.website && (
                      <span>
                        <span className="font-medium">Website:</span>{' '}
                        <a href={selectedSupplier.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedSupplier.website}</a>
                      </span>
                    )}
                  </div>
                  {selectedSupplier.notes && <p className="mt-2 text-sm text-gray-500 italic">{selectedSupplier.notes}</p>}
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEditSupplier(selectedSupplier)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded" title="Edit supplier">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDeleteSupplier(selectedSupplier)} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete supplier">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
              <div className="border-b border-gray-200 px-4 flex items-center justify-between">
                <div className="flex">
                  {['contracts', 'invoices'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      {tab} ({tab === 'contracts' ? contracts.length : invoices.length})
                    </button>
                  ))}
                </div>
                {activeTab === 'contracts' && (
                  <button
                    onClick={() => { setShowContractForm(true); setEditingContract(null); setContractForm({ ...emptyContract, supplierId: selectedSupplier.id }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Add Contract
                  </button>
                )}
                {activeTab === 'invoices' && (
                  <button
                    onClick={() => { setShowInvoiceForm(true); setEditingInvoice(null); setInvoiceForm({ ...emptyInvoice, supplierId: selectedSupplier.id }); setInvoiceFile(null); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Add Invoice
                  </button>
                )}
              </div>

              {/* Contracts tab */}
              {activeTab === 'contracts' && (
                <div className="p-4">
                  {contracts.length === 0 ? (
                    <div className="py-12 text-center text-gray-400">
                      <DocumentTextIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No contracts yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <th className="pb-3 pr-4">Service</th>
                            <th className="pb-3 pr-4">Cost</th>
                            <th className="pb-3 pr-4">Period</th>
                            <th className="pb-3 pr-4">Status</th>
                            <th className="pb-3"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {contracts.map((c) => (
                            <tr key={c.id} className="hover:bg-gray-50">
                              <td className="py-3 pr-4">
                                <p className="font-medium text-gray-900">{c.serviceName}</p>
                                {c.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{c.description}</p>}
                              </td>
                              <td className="py-3 pr-4">
                                {c.costAmount != null ? (
                                  <div>
                                    <span className="font-medium text-gray-900">{formatCurrency(c.costAmount, c.currency)}</span>
                                    <span className="text-xs text-gray-500 ml-1">/ {BILLING_CYCLE_LABELS[c.billingCycle]}</span>
                                  </div>
                                ) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="py-3 pr-4">
                                <p className="text-gray-700">{formatDate(c.contractStartDate)}</p>
                                {c.contractEndDate && <p className="text-xs text-gray-500">to {formatDate(c.contractEndDate)}</p>}
                                {c.autoRenew && <span className="text-xs text-blue-600">Auto-renew</span>}
                              </td>
                              <td className="py-3 pr-4">{contractStatusBadge(c.status)}</td>
                              <td className="py-3">
                                <div className="flex gap-1">
                                  <button onClick={() => handleEditContract(c)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => handleDeleteContract(c)} className="p-1 text-gray-400 hover:text-red-600 rounded">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Invoices tab */}
              {activeTab === 'invoices' && (
                <div className="p-4">
                  {invoices.length === 0 ? (
                    <div className="py-12 text-center text-gray-400">
                      <DocumentTextIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No invoices yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <th className="pb-3 pr-4">Invoice #</th>
                            <th className="pb-3 pr-4">Date</th>
                            <th className="pb-3 pr-4">Amount</th>
                            <th className="pb-3 pr-4">Status</th>
                            <th className="pb-3 pr-4">File</th>
                            <th className="pb-3"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {invoices.map((inv) => (
                            <tr key={inv.id} className="hover:bg-gray-50">
                              <td className="py-3 pr-4">
                                <p className="font-medium text-gray-900">{inv.invoiceNumber || `Invoice ${inv.id}`}</p>
                                {inv.contractServiceName && <p className="text-xs text-gray-500 mt-0.5">{inv.contractServiceName}</p>}
                              </td>
                              <td className="py-3 pr-4">
                                <p className="text-gray-700">{formatDate(inv.invoiceDate)}</p>
                                {inv.dueDate && <p className="text-xs text-gray-500">Due {formatDate(inv.dueDate)}</p>}
                              </td>
                              <td className="py-3 pr-4 font-medium text-gray-900">{formatCurrency(inv.amount, inv.currency)}</td>
                              <td className="py-3 pr-4">{invoiceStatusBadge(inv.status)}</td>
                              <td className="py-3 pr-4">
                                {inv.hasFile ? (
                                  <button onClick={() => handleDownloadInvoice(inv)} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs">
                                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                    {formatBytes(inv.fileSize)}
                                  </button>
                                ) : <span className="text-gray-400 text-xs">—</span>}
                              </td>
                              <td className="py-3">
                                <div className="flex gap-1">
                                  <button onClick={() => handleEditInvoice(inv)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => handleDeleteInvoice(inv)} className="p-1 text-gray-400 hover:text-red-600 rounded">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-200 border-dashed">
            <div className="text-center text-gray-400">
              <BuildingStorefrontIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium">Select a supplier</p>
              <p className="text-xs mt-1">Click a supplier on the left to view their contracts and invoices</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Supplier form modal ─────────────────────────────────────────────── */}
      {showSupplierForm && (
        <Modal title={editingSupplier ? 'Edit Supplier' : 'Add Supplier'} onClose={() => { setShowSupplierForm(false); setEditingSupplier(null); }}>
          <form onSubmit={handleSupplierSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input required className="input-field" value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input className="input-field" value={supplierForm.contactName} onChange={(e) => setSupplierForm({ ...supplierForm, contactName: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input type="email" className="input-field" value={supplierForm.contactEmail} onChange={(e) => setSupplierForm({ ...supplierForm, contactEmail: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input className="input-field" value={supplierForm.contactPhone} onChange={(e) => setSupplierForm({ ...supplierForm, contactPhone: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input type="url" className="input-field" value={supplierForm.website} onChange={(e) => setSupplierForm({ ...supplierForm, website: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={3} className="input-field resize-none" value={supplierForm.notes} onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} />
              </div>
            </div>
            <ModalActions onCancel={() => { setShowSupplierForm(false); setEditingSupplier(null); }} saving={savingSupplier} />
          </form>
        </Modal>
      )}

      {/* ── Contract form modal ─────────────────────────────────────────────── */}
      {showContractForm && (
        <Modal title={editingContract ? 'Edit Contract' : 'Add Contract'} onClose={() => { setShowContractForm(false); setEditingContract(null); }}>
          <form onSubmit={handleContractSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                <input required className="input-field" value={contractForm.serviceName} onChange={(e) => setContractForm({ ...contractForm, serviceName: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} className="input-field resize-none" value={contractForm.description} onChange={(e) => setContractForm({ ...contractForm, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                <input type="number" min="0" step="0.01" className="input-field" value={contractForm.costAmount} onChange={(e) => setContractForm({ ...contractForm, costAmount: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select className="input-field" value={contractForm.currency} onChange={(e) => setContractForm({ ...contractForm, currency: e.target.value })}>
                  <option>ZAR</option><option>USD</option><option>EUR</option><option>GBP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select className="input-field" value={contractForm.billingCycle} onChange={(e) => setContractForm({ ...contractForm, billingCycle: parseInt(e.target.value) })}>
                  <option value={1}>Monthly</option>
                  <option value={2}>Quarterly</option>
                  <option value={3}>Annual</option>
                  <option value={4}>One-off</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select className="input-field" value={contractForm.status} onChange={(e) => setContractForm({ ...contractForm, status: parseInt(e.target.value) })}>
                  <option value={1}>Active</option>
                  <option value={4}>Pending</option>
                  <option value={2}>Expired</option>
                  <option value={3}>Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" className="input-field" value={contractForm.contractStartDate} onChange={(e) => setContractForm({ ...contractForm, contractStartDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="date" className="input-field" value={contractForm.contractEndDate} onChange={(e) => setContractForm({ ...contractForm, contractEndDate: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="autoRenew" checked={contractForm.autoRenew} onChange={(e) => setContractForm({ ...contractForm, autoRenew: e.target.checked })} className="rounded border-gray-300" />
                <label htmlFor="autoRenew" className="text-sm text-gray-700">Auto-renew</label>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} className="input-field resize-none" value={contractForm.notes} onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })} />
              </div>
            </div>
            <ModalActions onCancel={() => { setShowContractForm(false); setEditingContract(null); }} saving={savingContract} />
          </form>
        </Modal>
      )}

      {/* ── Invoice form modal ──────────────────────────────────────────────── */}
      {showInvoiceForm && (
        <Modal title={editingInvoice ? 'Edit Invoice' : 'Add Invoice'} onClose={() => { setShowInvoiceForm(false); setEditingInvoice(null); }}>
          <form onSubmit={handleInvoiceSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                <input className="input-field" value={invoiceForm.invoiceNumber} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract (optional)</label>
                <select className="input-field" value={invoiceForm.supplierContractId} onChange={(e) => setInvoiceForm({ ...invoiceForm, supplierContractId: e.target.value })}>
                  <option value="">— None —</option>
                  {contracts.map((c) => <option key={c.id} value={c.id}>{c.serviceName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                <input type="date" className="input-field" value={invoiceForm.invoiceDate} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" className="input-field" value={invoiceForm.dueDate} onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input required type="number" min="0" step="0.01" className="input-field" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select className="input-field" value={invoiceForm.currency} onChange={(e) => setInvoiceForm({ ...invoiceForm, currency: e.target.value })}>
                  <option>ZAR</option><option>USD</option><option>EUR</option><option>GBP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select className="input-field" value={invoiceForm.status} onChange={(e) => setInvoiceForm({ ...invoiceForm, status: parseInt(e.target.value) })}>
                  <option value={1}>Pending</option>
                  <option value={2}>Paid</option>
                  <option value={3}>Overdue</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} className="input-field resize-none" value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} />
              </div>
              {!editingInvoice && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice File (PDF)</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
              )}
            </div>
            <ModalActions onCancel={() => { setShowInvoiceForm(false); setEditingInvoice(null); }} saving={savingInvoice} />
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── shared sub-components ─────────────────────────────────────────────────────

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({ onCancel, saving }) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
      <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
